import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { BidBreadcrumbs } from '@/features/bids/components/bid-breadcrumbs';
import { useEffect } from 'react';
import { useAuth } from '@/features/auth';
import { createFileRoute } from '@tanstack/react-router';
import { resourceLatestVersionQuery } from '@/features/workspaces/queries';
import { BidOverview } from '@/features/bids/components/bid-overview';
import { H1, H2 } from '@/ui/components/heading';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainHeaderBackButton,
  MainNav,
  MainNavButton,
  MainNavItem,
  MainNavLink,
  MainNavList,
} from '@/ui/components/main';
import { bidDetailsQuery } from '@/features/bids/queries';
import { useUpdateBid } from '@/features/bids/mutations';
import { useDocumentsIndexingStatus, entityDocumentsQuery } from '@/features/documents/queries';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { MainScrollArea } from '@/ui/components/main';
import {
  FolderIcon,
  NewOfficeIcon,
  PlusSignIcon,
  ScrollIcon,
  SettingsIcon,
  TimeQuarterPassIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { useProjectView } from '@/features/projects/helpers';
import { SummaryFrame } from '@/features/summary/components/summary-frame';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { versionPanelOpenAtom } from '@/features/workspaces/store';
import { chatSidePanelOpenAtom } from '@/features/chat/store';
import { OrganizationAvatar } from '@/features/organizations/components/organization-avatar';
import { Badge } from '@/ui/components/badge';
import { BID_OVERVIEW_TABS } from '@/features/bids/constants';
import { BidCvAnalysisFrame } from '@/features/bid-cvs/components/bid-cv-analysis-frame';
import { BidMethodologyFrame } from '@/features/bid-methodology/components/bid-methodology-frame';
import { CreateBid } from '@/features/bids/components/create-bid';
import { createBidOpenAtom } from '@/features/bids/store';
import { Button } from '@/ui/components/button';
import { Editable, EditableArea, EditableInput, EditablePreview } from '@/ui/components/editable';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import z from 'zod';
import { Group, GroupItem } from '@/ui/components/group';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/',
)({
  component: RouteComponent,
  validateSearch: z.object({
    tab: z.enum(BID_OVERVIEW_TABS).default('compliance').catch('compliance'),
  }),
});

function RouteComponent() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  const auth = useAuth();
  const organizationName = auth.user?.organizationName || '';
  const { data: bid } = useSuspenseQuery(bidDetailsQuery(bidId));
  const view = useProjectView();
  const setCreateBidOpen = useSetAtom(createBidOpenAtom);
  const updateBid = useUpdateBid();

  const { areDocumentsIndexed: isBidIndexed } = useDocumentsIndexingStatus({
    documentType: 'bid',
    id: bidId,
  });
  const { areDocumentsIndexed: isTenderPackageIndexed, hasNoDocuments: tenderHasNoDocuments } =
    useDocumentsIndexingStatus({
      documentType: 'tenderPackage',
      id: workspaceId,
    });

  const isTenderPackageReady = isTenderPackageIndexed && !tenderHasNoDocuments;
  const tenderPackageTooltip = tenderHasNoDocuments
    ? t(common.noTenderPackageDocuments)
    : !isTenderPackageIndexed
      ? t(common.waitForTenderPackageDocumentsIndexing)
      : undefined;

  const areDocumentsIndexed = isBidIndexed && isTenderPackageReady;
  const indexingTooltip = tenderPackageTooltip ?? t(common.waitForBidDocumentsIndexing);

  const setVersionPanelOpen = useSetAtom(versionPanelOpenAtom);
  const createdFromDraft = !!bid.sourceBidDraftDocumentId;

  useEffect(() => () => setVersionPanelOpen(false), [setVersionPanelOpen]);

  return (
    <>
      <Header>
        <HeaderBackButton
          to={
            view === 'supplier'
              ? '/project/$projectId/workspaces/$workspaceId'
              : '/project/$projectId/workspaces/$workspaceId/bids'
          }
          params={{ projectId, workspaceId }}
        />
        <BidBreadcrumbs />
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <IndexingStatusAlerts resourceId={bidId} documentType="bid" waitForKg />
        <IndexingStatusAlerts resourceId={workspaceId} documentType="tenderPackage" waitForKg />
        <MainHeader className="justify-start gap-1.5">
          <H1 className="min-w-0">
            <MainHeaderBackButton
              className="mr-1"
              to={
                view === 'supplier'
                  ? '/project/$projectId/workspaces/$workspaceId'
                  : '/project/$projectId/workspaces/$workspaceId/bids'
              }
              params={{ projectId, workspaceId }}
            />
            <Editable
              value={bid.bidAdditionalName ?? ''}
              maxLength={60}
              autosize
              onSubmit={(next) => {
                const trimmed = next.trim();
                if (!trimmed || trimmed === bid.bidAdditionalName) return;
                updateBid.mutate({
                  bidAdditionalName: trimmed,
                  companyId: bid.companyId ?? null,
                  additionalInfo: bid.additionalInfo ?? '',
                  bidType: bid.bidType!,
                });
              }}
            >
              <EditableArea>
                <EditablePreview className="block h-9 rounded-md px-1.5 py-0 text-2xl md:text-2xl leading-9 hover:bg-surface-2">
                  {bid.bidAdditionalName}
                </EditablePreview>
                <EditableInput
                  aria-label={t({ en: 'Name', nb: 'Navn' })}
                  className="h-9 px-1.5 text-2xl font-medium"
                />
              </EditableArea>
            </Editable>
          </H1>
          <Badge size="xxs" variant="tertiary" className="mt-1.5 max-md:hidden">
            {bid.bidType ? t(common.bidTypes[bid.bidType]) : null}
          </Badge>
          <Group className="md:ml-auto max-md:w-full">
            {view === 'supplier' && (
              <Tooltip>
                <TooltipTrigger
                  delay={0}
                  render={
                    <GroupItem
                      render={
                        <Button
                          className="max-md:w-full"
                          variant="secondary"
                          kind="with-icon"
                          disabled={tenderHasNoDocuments || !isTenderPackageIndexed}
                          onClick={() => setCreateBidOpen(true)}
                        >
                          <Icon icon={PlusSignIcon} />
                          {t({
                            en: 'New bid',
                            nb: 'Nytt tilbud',
                          })}
                        </Button>
                      }
                    />
                  }
                />
                {tenderHasNoDocuments ? (
                  <TooltipPopup>{t(common.noTenderPackageDocuments)}</TooltipPopup>
                ) : !isTenderPackageIndexed ? (
                  <TooltipPopup>
                    {t({
                      en: 'Please wait until tender package files are analysed before creating a bid.',
                      nb: 'Vent til anbudspakkefilene er analysert før du oppretter et tilbud.',
                    })}
                  </TooltipPopup>
                ) : null}
              </Tooltip>
            )}
          </Group>
        </MainHeader>
        <MainNav>
          <MainNavList>
            {bid.companyId && (
              <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                {organizationName === bid.companyName ? (
                  <OrganizationAvatar
                    organization={{ id: auth.user.organizationId, name: organizationName }}
                    size={24}
                    className="rounded-[calc(var(--radius-md)-0.125rem)]"
                  />
                ) : (
                  <Icon icon={NewOfficeIcon} />
                )}
                {bid.companyName}{' '}
                {organizationName === bid.companyName
                  ? `(${t({ en: 'your org', nb: 'ditt organisasjon' })})`
                  : null}
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/files"
                params={{ projectId, workspaceId, bidId }}
              >
                <Icon icon={FolderIcon} />
                {t({ en: 'Files', nb: 'Filer' })}{' '}
                <SuspenseBoundary>
                  <BidFilesCount />
                </SuspenseBoundary>
              </MainNavLink>
            </MainNavItem>
            {createdFromDraft && (
              <MainNavItem>
                <MainNavLink
                  to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/draft"
                  params={{ projectId, workspaceId, bidId }}
                  search={{ tab: 'Draft' }}
                >
                  <Icon icon={ScrollIcon} />
                  {t({
                    en: 'Created from Draft execution plan — open',
                    nb: 'Opprettet fra utkast til gjennomføringsplan – åpne',
                  })}
                </MainNavLink>
              </MainNavItem>
            )}
            <MainNavItem>
              <MainNavLink
                to="/project/$projectId/workspaces/$workspaceId/bids/$bidId/settings"
                params={{ projectId, workspaceId, bidId }}
              >
                <Icon icon={SettingsIcon} />
                {t({ en: 'Settings', nb: 'Innstillinger' })}
              </MainNavLink>
            </MainNavItem>
            <SuspenseBoundary>
              <VersionHistoryNav />
            </SuspenseBoundary>
          </MainNavList>
        </MainNav>
        <MainContainer>
          <H2 className="mb-3">{t({ en: 'Overview', nb: 'Oversikt' })}</H2>
          <BidOverview />
          <div className="mt-5 space-y-4">
            <BidMethodologyFrame
              enableAnalysis={isTenderPackageReady}
              disabledTooltip={tenderPackageTooltip}
            />
            <BidCvAnalysisFrame
              enableAnalysis={areDocumentsIndexed}
              disabledTooltip={!areDocumentsIndexed ? indexingTooltip : undefined}
            />
          </div>
          <div>
            <H2 className="mb-3 mt-5">{t({ en: 'Analysis areas', nb: 'Identifiser' })}</H2>
            <MainFrames className="@4xl:grid-cols-1 @8xl:grid-cols-2 @7xl:grid-cols-2">
              <SummaryFrame />
            </MainFrames>
          </div>
        </MainContainer>
      </MainScrollArea>
      <CreateBid />
      <FileUploadDialog resourceId={bidId} resourceType="bid" />
    </>
  );
}

function BidFilesCount() {
  const { bidId } = Route.useParams();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(bidId, 'bid'));
  if (!documents.length) return null;
  return `(${documents.length})`;
}

function VersionHistoryNav() {
  const { bidId } = Route.useParams();
  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const setSidechatOpen = useSetAtom(chatSidePanelOpenAtom);
  const { data: latestVersion } = useSuspenseQuery(resourceLatestVersionQuery(bidId));
  if (!latestVersion) return null;
  return (
    <MainNavItem>
      <MainNavButton
        data-selected={versionPanelOpen ? '' : undefined}
        onClick={(e) => {
          e.preventDefault();
          setVersionPanelOpen((prev) => !prev);
          if (!versionPanelOpen) setSidechatOpen(false);
        }}
      >
        <Icon icon={TimeQuarterPassIcon} />
        {t({ en: 'Version history', nb: 'Versjonshistorikk' })} (v
        {latestVersion.version.versionNumber}{' '}
        {latestVersion.version.status === 'active' ? t({ en: 'latest', nb: 'siste' }) : ''})
      </MainNavButton>
    </MainNavItem>
  );
}
